import {
  Button,
  Cell,
  Column,
  MenuItem,
  MenuTrigger,
  Row,
  Table,
  TableBody,
  TableFooter,
  TableHeader,
} from '@eleveo/components-react';
import { PencilIcon, ThreeDotsIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { parseDate } from '@internationalized/date';
import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';

import { useAuth, Permissions } from '../../app/AuthorizationProvider';
import { AvatarLabel } from '../../components/LayoutNext/components/AvatarLabel';
import { MultipleItemsLabel } from '../../components/LayoutNext/components/MultipleItemsLabel';
import { fullNameOfAgent } from '../../utils/helpers';
import { getAgentPropTag, LIST_OF_AGENTS_CONTAINER_TAG } from './constants';
import { Agent } from './types';

import type { ColumnProps } from '@eleveo/components-react';

type AgentsTableTypes = {
  agents: Agent[];
  onEditAgent: (agent: Agent) => void;
  pagination: React.ReactNode;
  activeId?: string;
};

export const AgentsTable: React.FC<AgentsTableTypes> = ({ activeId, agents, onEditAgent, pagination }) => {
  const { userHasPermissions, timezone } = useAuth();
  const { t } = useTranslation();
  const formatter = useDateFormatter({ dateStyle: 'medium', timeZone: timezone });

  const hasEditPermission = userHasPermissions([Permissions.EditAgent]);
  const noPermission = !hasEditPermission;

  const columns: (ColumnProps<any> & { key: React.Key })[] = [
    { key: 'name', children: t('person.name') },
    { key: 'team', children: t('team.team', { count: 2 }) },
    { key: 'queue', children: t('queue.queues') },
    { key: 'skill', children: t('skill.skills') },
    {
      key: 'shiftTemplate',
      children: t('shiftTemplate.shiftTemplatesWithEffectiveDate'),
    },
  ];

  if (!noPermission) {
    columns.push({
      key: 'actions',
      children: t('layout.action.actions'),
      isActions: true,
      hideHeader: true,
    });
  }

  return (
    <Table
      aria-label={t('person.people')}
      highlightedKey={activeId}
      isStickyHeader
      testId={LIST_OF_AGENTS_CONTAINER_TAG}
    >
      <TableHeader columns={columns}>{(column) => <Column {...column} key={column.key} />}</TableHeader>
      <TableBody items={agents}>
        {(agent) => (
          <Row>
            {(columnKey) => {
              /* eslint-disable no-case-declarations */
              switch (columnKey) {
                case 'name':
                  return (
                    <Cell>
                      <AvatarLabel name={fullNameOfAgent(agent)} isColorized />
                    </Cell>
                  );
                case 'queue':
                  return (
                    <Cell>
                      <MultipleItemsLabel items={agent.queues} />
                    </Cell>
                  );
                case 'skill':
                  return (
                    <Cell>
                      <MultipleItemsLabel items={agent.skills} />
                    </Cell>
                  );
                case 'team':
                  return (
                    <Cell>
                      <MultipleItemsLabel items={agent.teams} />
                    </Cell>
                  );
                case 'shiftTemplate':
                  return (
                    <Cell>
                      <MultipleItemsLabel
                        items={agent.shiftTemplates?.map((shiftTemplate) => ({
                          ...shiftTemplate,
                          name: `${shiftTemplate.name} (${formatter.format(
                            parseDate(shiftTemplate.effectiveDate).toDate(timezone)
                          )})`,
                        }))}
                      />
                    </Cell>
                  );
                case 'actions':
                  return (
                    <Cell>
                      <MenuTrigger
                        items={
                          [
                            hasEditPermission && {
                              id: '1',
                              icon: <PencilIcon />,
                              onClick: () => onEditAgent(agent),
                              testId: getAgentPropTag('edit'),
                              text: t('layout.action.edit'),
                            },
                          ].filter(Boolean) as MenuItem[]
                        }
                      >
                        <Button
                          aria-label={t('layout.action.actions')}
                          isDisabled={noPermission}
                          isSmall
                          testId={getAgentPropTag('actions')}
                          variant="quiet"
                        >
                          <ThreeDotsIcon />
                        </Button>
                      </MenuTrigger>
                    </Cell>
                  );
                default:
                  return <Cell>&nbsp;</Cell>;
              }
            }}
          </Row>
        )}
      </TableBody>
      <TableFooter>{pagination}</TableFooter>
    </Table>
  );
};
